import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message, Dropdown } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getTickets,
  deleteTicket,
  closeTicket,
  reopenTicket,
  type Ticket,
  type TicketFilters,
} from '@/api/ticketApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  in_progress: 'processing',
  pending: 'orange',
  resolved: 'green',
  closed: 'default',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  critical: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  inquiry: 'cyan',
  issue: 'volcano',
  feature_request: 'purple',
};

export default function TicketListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<TicketFilters>({});

  const canWrite = hasPermission('tickets', 'write');
  const canDelete = hasPermission('tickets', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof TicketFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getTickets({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('tickets.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteTicket(id);
            message.success(t('tickets.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleLifecycle = useCallback(
    async (action: 'close' | 'reopen', id: string) => {
      try {
        if (action === 'close') await closeTicket(id);
        else await reopenTicket(id);
        message.success(t(`tickets.${action}_success`));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<Ticket> = useMemo(
    () => [
      {
        title: t('tickets.field.subject'),
        dataIndex: 'subject',
        key: 'subject',
        sorter: true,
        ellipsis: true,
        render: (subject: string, record: Ticket) => (
          <a onClick={() => navigate(`/tickets/${record.id}`)}>{subject}</a>
        ),
      },
      {
        title: t('tickets.field.type'),
        dataIndex: 'type',
        key: 'type',
        width: 130,
        render: (type: string) => (
          <Tag color={TYPE_COLORS[type] ?? 'default'}>
            {t(`tickets.type_options.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('tickets.field.priority'),
        dataIndex: 'priority',
        key: 'priority',
        width: 100,
        render: (priority: string) => (
          <Tag color={PRIORITY_COLORS[priority] ?? 'default'}>
            {t(`tickets.priority_options.${priority}`, priority)}
          </Tag>
        ),
      },
      {
        title: t('tickets.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`tickets.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('tickets.field.customer_id'),
        dataIndex: 'customer_id',
        key: 'customer_id',
        ellipsis: true,
        width: 140,
      },
      {
        title: t('tickets.field.assigned_to'),
        dataIndex: 'assigned_to',
        key: 'assigned_to',
        ellipsis: true,
        width: 130,
      },
      {
        title: t('tickets.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 180,
        render: (_: unknown, record: Ticket) => {
          const lifecycleItems = [];
          if (record.status !== 'closed' && record.status !== 'resolved' && canWrite) {
            lifecycleItems.push({
              key: 'close',
              icon: <CloseCircleOutlined />,
              label: t('tickets.action.close'),
              onClick: () => handleLifecycle('close', record.id),
            });
          }
          if ((record.status === 'resolved' || record.status === 'closed') && canWrite) {
            lifecycleItems.push({
              key: 'reopen',
              icon: <UndoOutlined />,
              label: t('tickets.action.reopen'),
              onClick: () => handleLifecycle('reopen', record.id),
            });
          }

          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/tickets/${record.id}`)}
              />
              {canWrite && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/tickets/${record.id}/edit`)}
                />
              )}
              {canDelete && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              )}
              {lifecycleItems.length > 0 && (
                <Dropdown menu={{ items: lifecycleItems }}>
                  <Button type="link" size="small" icon={<DownOutlined />} />
                </Dropdown>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleLifecycle]
  );

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('tickets.filter.all') },
      { value: 'inquiry', label: t('tickets.type_options.inquiry') },
      { value: 'issue', label: t('tickets.type_options.issue') },
      { value: 'feature_request', label: t('tickets.type_options.feature_request') },
    ],
    [t]
  );

  const priorityOptions = useMemo(
    () => [
      { value: '', label: t('tickets.filter.all') },
      { value: 'low', label: t('tickets.priority_options.low') },
      { value: 'medium', label: t('tickets.priority_options.medium') },
      { value: 'high', label: t('tickets.priority_options.high') },
      { value: 'critical', label: t('tickets.priority_options.critical') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('tickets.filter.all') },
      { value: 'open', label: t('tickets.status_options.open') },
      { value: 'in_progress', label: t('tickets.status_options.in_progress') },
      { value: 'pending', label: t('tickets.status_options.pending') },
      { value: 'resolved', label: t('tickets.status_options.resolved') },
      { value: 'closed', label: t('tickets.status_options.closed') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('tickets.filter.type')}
        style={{ width: 150 }}
        allowClear
        options={typeOptions}
        onChange={(val) => handleFilterChange('type', val || undefined)}
      />
      <Select
        placeholder={t('tickets.filter.priority')}
        style={{ width: 120 }}
        allowClear
        options={priorityOptions}
        onChange={(val) => handleFilterChange('priority', val || undefined)}
      />
      <Select
        placeholder={t('tickets.filter.status')}
        style={{ width: 130 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/tickets/new')}
        >
          {t('tickets.new_ticket')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('tickets.list_title')}</h2>
      <DataGrid<Ticket>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('tickets.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/tickets/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
